import moment from 'moment';
import _isNil from 'lodash/isNil';
import _isArray from 'lodash/isArray';
import _omit from 'lodash/omit';
import { extensions } from '../../redux/util/constants';
import { MrnConfig } from '../../common/types/Projects';

export type Gender = 'male' | 'female' | 'other' | 'unknown';
const genders: Gender[] = ['male', 'female', 'other', 'unknown'];

export const fhirDateFormats = {
  date: 'YYYY-MM-DD',
  dateTime: 'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
};

export const formDateFormats = {
  date: 'MM/DD/YYYY',
  dateTime: 'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
};

export interface NameFormValue {
  firstName: string;
  lastName: string;
}

export interface AddressFormValue {
  text: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}

export type RaceCodes =
  | '1002-5'
  | '2028-9'
  | '2054-5'
  | '2076-8'
  | '2106-3'
  | '2131-1';

export const allRaceCodes: RaceCodes[] = [
  '1002-5',
  '2028-9',
  '2054-5',
  '2076-8',
  '2106-3',
  '2131-1',
];

export const raceDisplay: { [key in RaceCodes]: string } = {
  '1002-5': 'American Indian or Alaska Native',
  '2028-9': 'Asian',
  '2054-5': 'Black or African American',
  '2076-8': 'Native Hawaiian or Other Pacific Islander',
  '2106-3': 'White',
  '2131-1': 'Other Race',
};

export type GenderIdentityCodes =
  | 'male'
  | 'female'
  | 'non-binary'
  | 'transgender-male'
  | 'transgender-female'
  | 'other'
  | 'non-disclose';

export const allGenderIdentityCodes: GenderIdentityCodes[] = [
  'male',
  'female',
  'non-binary',
  'transgender-male',
  'transgender-female',
  'other',
  'non-disclose',
];

export const genderIdentityDisplay: { [key in GenderIdentityCodes]: string } = {
  male: 'male',
  female: 'female',
  'non-binary': 'non-binary',
  'transgender-male': 'transgender-male',
  'transgender-female': 'transgender-female',
  other: 'other',
  'non-disclose': 'non-disclose',
};

export interface PatientDataFormValue {
  subjectId: string;
  mrn: string[] | '';
  name: NameFormValue;
  address: AddressFormValue;
  email: string;
  phoneNumber: string;
  dob: string;
  dod: string;
  gender: Gender | '';
  race: RaceCodes | '';
  genderIdentity: GenderIdentityCodes | '';
  socialSecurityNumber: string | '';
}

interface Mapper<TFormValue> {
  toFormValue: (patient: fhir.Patient) => TFormValue;
  fromFormValue: (
    formValue: TFormValue,
    originalPatient: fhir.Patient,
  ) => fhir.Patient;
}

const toArray = (field: any) => (Array.isArray(field) ? field : [field]);

class FhirArrayUpdater<TArrayElement extends Record<string, any>> {
  private _arr: TArrayElement[];
  private _elementSelector: (el: TArrayElement) => boolean;
  private _defaultElement: Partial<TArrayElement>;
  private _indexOfElement: number;

  constructor(
    arr: TArrayElement[],
    elementSelector: (el: TArrayElement) => boolean,
    defaultElement: Partial<TArrayElement>,
    fallbackElementSelector?: (el: TArrayElement, index?: number) => boolean,
  ) {
    this._arr = arr || [];
    this._elementSelector = elementSelector || (() => false);
    this._defaultElement = defaultElement || {};
    this._indexOfElement = this._arr.findIndex(this._elementSelector);
    if (this._indexOfElement === -1) {
      this._indexOfElement = this._arr.findIndex(
        fallbackElementSelector || (() => false),
      );
    }
  }

  get originalElement() {
    return this._indexOfElement === -1
      ? this._defaultElement
      : this._arr[this._indexOfElement];
  }

  updateArray(updatedElementProps: Partial<TArrayElement>): TArrayElement[] {
    const originalElement = this.originalElement;
    let updated = {
      ...originalElement,
      ...updatedElementProps,
    } as TArrayElement;

    // remove null or empty array props
    const omissionProps: string[] = [];
    Object.keys(updated).forEach(key => {
      const val = (updated as any)[key];
      if (_isNil(val) || val === '' || (_isArray(val) && !val.length)) {
        omissionProps.push(key);
      }
    });
    updated = _omit(
      updated as Record<string, any>,
      ...omissionProps,
    ) as TArrayElement;

    if (this._indexOfElement === -1) {
      return [...this._arr, updated];
    } else {
      const copied = [...this._arr];
      copied[this._indexOfElement] = updated;
      return copied;
    }
  }

  deleteElement(): TArrayElement[] {
    if (this._indexOfElement === -1) {
      return this._arr;
    }
    const copied = [...this._arr];
    copied.splice(this._indexOfElement, 1);
    return copied;
  }
}

export class PatientNameMapper implements Mapper<NameFormValue> {
  private readonly _defaultName: Partial<fhir.HumanName> = {
    use: 'official',
    given: [],
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    family: null,
  };
  private readonly _nameSelector = (name: fhir.HumanName) =>
    (name || {}).use === 'official';

  // If there is no official name, select the first element in the name array
  // This will still return the desired -1 on an empty array
  private readonly _fallbackNameSelector = (
    _name: fhir.HumanName,
    index: number,
  ) => index === 0;

  toFormValue(patient: fhir.Patient): NameFormValue {
    const updater = new FhirArrayUpdater<fhir.HumanName>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (patient || {}).name,
      this._nameSelector,
      this._defaultName,
      this._fallbackNameSelector,
    );

    const name = updater.originalElement;
    const firstName = (name.given || []).join(' ') || '';
    const lastName = name.family || '';
    return { firstName, lastName };
  }

  fromFormValue(
    formValue: NameFormValue,
    originalPatient: fhir.Patient,
  ): fhir.Patient {
    const updater = new FhirArrayUpdater<fhir.HumanName>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (originalPatient || {}).name,
      this._nameSelector,
      this._defaultName,
    );
    const safeFormValue = formValue || ({} as NameFormValue);
    const updatedName: fhir.HumanName = {
      ...updater.originalElement,
      given: (safeFormValue.firstName || '').split(' ').filter(Boolean) || [],
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      family: safeFormValue.lastName || null,
    };

    return {
      ...originalPatient,
      name: updater.updateArray(updatedName),
    };
  }
}

export class AddressMapper implements Mapper<AddressFormValue> {
  private readonly _defaultAddress: Partial<fhir.Address> = {
    use: 'home',
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    city: null,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    state: null,
    line: [],
  };
  private readonly _addressSelector = (address: fhir.Address) =>
    (address || {}).use === 'home';

  toFormValue(patient: fhir.Patient): AddressFormValue {
    const updater = new FhirArrayUpdater<fhir.Address>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (patient || {}).address,
      this._addressSelector,
      this._defaultAddress,
    );
    const address = updater.originalElement;
    const city = address.city || '';
    const state = address.state || '';
    const text = (address.line || []).join(', ');
    const postalCode = address.postalCode || '';
    const country = address.country || '';
    return { city, state, country, text, postalCode };
  }

  fromFormValue(
    formValue: AddressFormValue,
    originalPatient: fhir.Patient,
  ): fhir.Patient {
    const updater = new FhirArrayUpdater<fhir.Address>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (originalPatient || {}).address,
      this._addressSelector,
      this._defaultAddress,
    );
    const safeFormValue = formValue || ({} as AddressFormValue);
    const updatedAddress: fhir.Address = {
      ...updater.originalElement,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      city: safeFormValue.city || null,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      state: safeFormValue.state || null,
      line: (safeFormValue.text || '').split(', ').filter(Boolean) || [],
      postalCode: safeFormValue.postalCode,
      country: safeFormValue.country,
    };

    return {
      ...originalPatient,
      address: updater.updateArray(updatedAddress),
    };
  }
}

type SupportedContactSystems = 'phone' | 'email';

export class ContactPointMapper implements Mapper<string> {
  private _defaultContactPoint: Partial<fhir.ContactPoint>;
  private _contactPointSelector: (contactPoint: fhir.ContactPoint) => boolean;

  constructor(system: SupportedContactSystems) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._defaultContactPoint = { system, value: null };
    this._contactPointSelector = (contactPoint: fhir.ContactPoint) =>
      (contactPoint || {}).system === system;
  }

  toFormValue(patient: fhir.Patient): string {
    const updater = new FhirArrayUpdater<fhir.ContactPoint>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (patient || {}).telecom,
      this._contactPointSelector,
      this._defaultContactPoint,
    );
    const contactPoint = updater.originalElement;
    return contactPoint.value || '';
  }

  fromFormValue(
    formValue: string,
    originalPatient: fhir.Patient,
  ): fhir.Patient {
    const updater = new FhirArrayUpdater<fhir.Address>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (originalPatient || {}).telecom,
      this._contactPointSelector,
      this._defaultContactPoint,
    );
    const updatedContactPoint: fhir.ContactPoint = {
      ...updater.originalElement,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      value: formValue || null,
    };

    return {
      ...originalPatient,
      telecom: updater.updateArray(updatedContactPoint),
    };
  }
}

export class DateMapper implements Mapper<string> {
  private _dateType: 'dob' | 'dod';
  constructor(dateType: 'dob' | 'dod') {
    this._dateType = dateType;
  }

  toFormValue(patient: fhir.Patient): string {
    const targetValue =
      this._dateType === 'dob'
        ? (patient || {}).birthDate
        : (patient || {}).deceasedDateTime;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!targetValue) return null;

    const dateFormat =
      this._dateType === 'dob'
        ? fhirDateFormats.date
        : fhirDateFormats.dateTime;
    const momentVal = moment(targetValue, dateFormat);

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return momentVal.isValid() ? momentVal.format(formDateFormats.date) : null;
  }

  fromFormValue(formValue: string, patient: fhir.Patient): fhir.Patient {
    const asMoment = moment(formValue, formDateFormats.date);
    const valueValid = formValue && asMoment.isValid && asMoment.isValid();

    const returnVal: fhir.Patient = { ...(patient || {}) };

    if (this._dateType === 'dob' && valueValid) {
      returnVal.birthDate = asMoment.format(fhirDateFormats.date);
    } else if (this._dateType === 'dob' && !valueValid) {
      delete returnVal.birthDate;
    } else if (this._dateType === 'dod' && valueValid) {
      delete returnVal.deceasedBoolean;
      returnVal.deceasedDateTime = asMoment.format(fhirDateFormats.dateTime);
    } else if (this._dateType === 'dod' && !valueValid) {
      delete returnVal.deceasedDateTime;
    }

    return returnVal;
  }
}

export class GenderMapper implements Mapper<Gender | ''> {
  toFormValue(patient: fhir.Patient): Gender | '' {
    const rawValue = ((patient || {}).gender || null) as Gender;
    const targetValue = genders.includes(rawValue) ? rawValue : '';
    return targetValue;
  }

  fromFormValue(formValue: Gender | '', patient: fhir.Patient): fhir.Patient {
    const returnVal: fhir.Patient = { ...(patient || {}) };
    const value =
      formValue !== '' && genders.includes(formValue) ? formValue : null;
    if (value) {
      returnVal.gender = value;
    } else {
      delete returnVal.gender;
    }

    return returnVal;
  }
}

export class SocialSecurityNumberMapper implements Mapper<string | ''> {
  private _defaultIdentifier: Partial<fhir.Identifier>;
  private _identifierSelector: (identifier: fhir.Identifier) => boolean;

  constructor() {
    this._defaultIdentifier = {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      value: null,
      type: {
        coding: [
          {
            system: 'http://hl7.org/fhir/v2/0203',
            code: 'SS',
          },
        ],
      },
      system: 'http://hl7.org/fhir/sid/us-ssn',
    };

    this._identifierSelector = (identifier: fhir.Identifier) =>
      !!(identifier?.type?.coding || []).find(_ => _.code === 'SS');
  }

  toFormValue(patient: fhir.Patient): string {
    const updater = new FhirArrayUpdater<fhir.ContactPoint>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (patient || {}).identifier,
      this._identifierSelector,
      this._defaultIdentifier,
    );
    const identifier = updater.originalElement;
    return identifier.value || '';
  }

  fromFormValue(
    formValue: string,
    originalPatient: fhir.Patient,
  ): fhir.Patient {
    const updater = new FhirArrayUpdater<fhir.Identifier>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (originalPatient || {}).identifier,
      this._identifierSelector,
      this._defaultIdentifier,
    );
    const updatedIdentifier: fhir.Identifier = {
      ...updater.originalElement,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      value: formValue || null,
    };

    return {
      ...originalPatient,
      identifier: updater.updateArray(updatedIdentifier),
    };
  }
}

export class SubjectIdMapper implements Mapper<string | ''> {
  private _defaultIdentifier: Partial<fhir.Identifier>;
  private _identifierSelector: (identifier: fhir.Identifier) => boolean;

  constructor() {
    this._defaultIdentifier = {
      system: 'lrn',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      value: null,
      type: {
        coding: [
          {
            code: 'ANON',
            system: 'http://hl7.org/fhir/v2/0203',
          },
        ],
      },
    };

    this._identifierSelector = (identifier: fhir.Identifier) =>
      !!(identifier?.type?.coding || []).find(_ => _.code === 'ANON');
  }

  toFormValue(patient: fhir.Patient): string {
    const updater = new FhirArrayUpdater<fhir.ContactPoint>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (patient || {}).identifier,
      this._identifierSelector,
      this._defaultIdentifier,
    );
    const identifier = updater.originalElement;
    return identifier.value || '';
  }

  fromFormValue(
    formValue: string,
    originalPatient: fhir.Patient,
  ): fhir.Patient {
    const updater = new FhirArrayUpdater<fhir.Identifier>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      (originalPatient || {}).identifier,
      this._identifierSelector,
      this._defaultIdentifier,
    );
    const updatedIdentifier: fhir.Identifier = {
      ...updater.originalElement,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      value: formValue || null,
    };

    return {
      ...originalPatient,
      identifier: updater.updateArray(updatedIdentifier),
    };
  }
}

export class MrnMapper implements Mapper<string[] | ''> {
  private _defaultIdentifier: Partial<fhir.Identifier>;
  private _identifierSelector: (identifier: fhir.Identifier) => boolean;
  private _identifierAndExtensionSelector: (
    identifier: fhir.Identifier,
  ) => boolean;

  constructor(mrnConfig?: any) {
    const configType = mrnConfig?.configType;

    const coding =
      configType === 'typeCoding'
        ? {
            code: mrnConfig.code,
            system: mrnConfig.system,
          }
        : {
            code: 'MR',
            system: 'http://hl7.org/fhir/v2/0203',
          };

    this._defaultIdentifier = {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      value: null,
      type: {
        coding: [coding],
      },
    };

    if (configType === 'system') {
      this._defaultIdentifier.system = mrnConfig?.system;
    }

    this._identifierSelector = (identifier: fhir.Identifier) => {
      const defaultCodeAndSystemFinder = (_: any) =>
        _.code === 'MR' && _.system === 'http://hl7.org/fhir/v2/0203';

      if (configType === 'system') {
        return !!(
          (identifier?.system === mrnConfig?.system &&
            identifier?.type?.coding) ||
          []
        ).find(defaultCodeAndSystemFinder);
      } else if (configType === 'typeCoding') {
        return !!(identifier?.type?.coding || []).find(
          (_: any) =>
            _.code === mrnConfig.code && _.system === mrnConfig.system,
        );
      } else {
        return !!(identifier?.type?.coding || []).find(
          defaultCodeAndSystemFinder,
        );
      }
    };

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._identifierAndExtensionSelector = (identifier: fhir.Identifier) => {
      return (
        identifier?.extension?.filter(
          extension =>
            extension.url ===
            'http://hl7.org/fhir/StructureDefinition/rendered-value',
        ) && this._identifierSelector(identifier)
      );
    };
  }

  toFormValue(patient: fhir.Patient): string[] | '' {
    const preferedValues =
      patient?.identifier?.filter(this._identifierAndExtensionSelector) || [];
    if (preferedValues.length) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return preferedValues.map(preferedValue => preferedValue.value);
    }

    const values = patient?.identifier?.filter(this._identifierSelector) || [];
    if (!values.length) {
      return '';
    }
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return values.map(value => value.value);
  }

  fromFormValue(
    newFormValueRaw: string[] | '',
    originalPatient: fhir.Patient,
  ): fhir.Patient {
    const oldValue = toArray(this.toFormValue(originalPatient));
    const newFormValue = toArray(newFormValueRaw);

    let identifier: fhir.Identifier[] = [];
    for (let index = 0; index < newFormValue.length; index++) {
      const identifierSelectorByValue = (identifier: fhir.Identifier) =>
        this._identifierSelector(identifier) &&
        identifier.value === oldValue[index];
      const updater = new FhirArrayUpdater<fhir.Identifier>(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        !index ? (originalPatient || {}).identifier : identifier,
        identifierSelectorByValue,
        this._defaultIdentifier,
      );

      if (newFormValue[index] === '') {
        identifier = updater.deleteElement();
        continue;
      }

      const newIdentifier = {
        ...updater.originalElement,
        value: newFormValue[index],
      };

      identifier = updater.updateArray(newIdentifier);
    }

    return {
      ...originalPatient,
      identifier,
    };
  }
}

export class SubjectRaceMapper implements Mapper<RaceCodes | ''> {
  private _extensionUrl = extensions.patient.hl7.fhir.usCore.usCoreRace;
  private _codeSystem = extensions.patient.hl7.fhir.codeSystem.race;

  private _isTargetExtension = (extension: fhir.Extension) =>
    extension?.url === this._extensionUrl && !!extension?.valueCodeableConcept;

  private _isTargetCoding = (coding: fhir.Coding) =>
    coding.system === this._codeSystem;

  toFormValue(patient: fhir.Patient): RaceCodes | '' {
    const targetExtension = (patient?.extension || []).find(
      this._isTargetExtension,
    );

    if (!targetExtension) return '';

    const targetCoding = targetExtension.valueCodeableConcept?.coding?.find(
      this._isTargetCoding,
    );

    if (!targetCoding) return '';

    // only support level 1 codes
    if (!allRaceCodes.includes(targetCoding?.code as RaceCodes)) return '';

    return targetCoding.code as RaceCodes;
  }

  fromFormValue(
    formValue: RaceCodes | '',
    originalPatient: fhir.Patient,
  ): fhir.Patient {
    // take precaution not to remove arrays of other system's extensions/codes if value is empty
    const originalRaceExtension = (originalPatient?.extension || []).find(
      _ => _?.url === this._extensionUrl && !!_?.valueCodeableConcept,
    );

    const originalCodings =
      originalRaceExtension?.valueCodeableConcept?.coding || [];

    const updatedCodings = originalCodings.filter(
      _ => !this._isTargetCoding(_),
    );
    if (formValue) {
      updatedCodings.push({
        system: this._codeSystem,
        code: formValue,
        display: raceDisplay[formValue],
      });
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    let updatedRaceExtension: fhir.Extension = null;
    if (updatedCodings.length) {
      updatedRaceExtension = {
        url: this._extensionUrl,
        valueCodeableConcept: {
          coding: updatedCodings,
        },
      };
    }

    const updatedExtensions: fhir.Extension[] = [
      ...(originalPatient?.extension || []).filter(
        _ => !this._isTargetExtension(_),
      ),
      updatedRaceExtension,
    ].filter(Boolean);

    const { extension, ...withoutExtension } = originalPatient;

    if (updatedExtensions.length) {
      return {
        ...withoutExtension,
        extension: updatedExtensions,
      };
    } else {
      return withoutExtension;
    }
  }
}

export class SubjectGenderIdentityMapper
  implements Mapper<GenderIdentityCodes | ''>
{
  private _extensionUrl = extensions.patient.hl7.fhir.genderIdentity;
  private _codeSystem = extensions.patient.hl7.fhir.codeSystem.genderIdentity;

  private _isTargetExtension = (extension: fhir.Extension) =>
    extension?.url === this._extensionUrl && !!extension?.valueCodeableConcept;

  private _isTargetCoding = (coding: fhir.Coding) =>
    coding.system === this._codeSystem;

  toFormValue(patient: fhir.Patient): GenderIdentityCodes | '' {
    const targetExtension = (patient?.extension || []).find(
      this._isTargetExtension,
    );

    if (!targetExtension) return '';

    const targetCoding = targetExtension.valueCodeableConcept?.coding?.find(
      this._isTargetCoding,
    );

    if (!targetCoding) return '';

    // only support level 1 codes
    if (
      !allGenderIdentityCodes.includes(
        targetCoding?.code as GenderIdentityCodes,
      )
    )
      return '';

    return targetCoding.code as GenderIdentityCodes;
  }

  fromFormValue(
    formValue: GenderIdentityCodes | '',
    originalPatient: fhir.Patient,
  ): fhir.Patient {
    // take precaution not to remove arrays of other system's extensions/codes if value is empty
    const originalGenderIdentityExtension = (
      originalPatient?.extension || []
    ).find(_ => _?.url === this._extensionUrl && !!_?.valueCodeableConcept);

    const originalCodings =
      originalGenderIdentityExtension?.valueCodeableConcept?.coding || [];

    const updatedCodings = originalCodings.filter(
      _ => !this._isTargetCoding(_),
    );
    if (formValue) {
      updatedCodings.push({
        system: this._codeSystem,
        code: formValue,
        display: genderIdentityDisplay[formValue],
      });
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    let updateGenderIdentityExtensions: fhir.Extension = null;
    if (updatedCodings.length) {
      updateGenderIdentityExtensions = {
        url: this._extensionUrl,
        valueCodeableConcept: {
          coding: updatedCodings,
        },
      };
    }

    const updatedExtensions: fhir.Extension[] = [
      ...(originalPatient?.extension || []).filter(
        _ => !this._isTargetExtension(_),
      ),
      updateGenderIdentityExtensions,
    ].filter(Boolean);

    const { extension, ...withoutExtension } = originalPatient;

    if (updatedExtensions.length) {
      return {
        ...withoutExtension,
        extension: updatedExtensions,
      };
    } else {
      return withoutExtension;
    }
  }
}

export const patientToFormValue = (patient: fhir.Patient, mrnConfig?: any) => {
  const subjectId = new SubjectIdMapper().toFormValue(patient);
  const mrn = new MrnMapper(mrnConfig).toFormValue(patient);
  const name = new PatientNameMapper().toFormValue(patient);
  const address = new AddressMapper().toFormValue(patient);
  const email = new ContactPointMapper('email').toFormValue(patient);
  const phoneNumber = new ContactPointMapper('phone').toFormValue(patient);
  const dob = new DateMapper('dob').toFormValue(patient);
  const dod = new DateMapper('dod').toFormValue(patient);
  const gender = new GenderMapper().toFormValue(patient);
  const race = new SubjectRaceMapper().toFormValue(patient);
  const genderIdentity = new SubjectGenderIdentityMapper().toFormValue(patient);
  const socialSecurityNumber = new SocialSecurityNumberMapper().toFormValue(
    patient,
  );

  return {
    subjectId,
    mrn,
    name,
    address,
    email,
    phoneNumber,
    dob,
    dod,
    gender,
    race,
    genderIdentity,
    socialSecurityNumber,
  };
};

export const formValueToPatient = (
  formValue: PatientDataFormValue,
  patient: fhir.Patient,
  mrnConfig: MrnConfig,
) => {
  const safeFormValue = formValue || ({} as PatientDataFormValue);
  let updatePatient: fhir.Patient = { ...patient, resourceType: 'Patient' };

  updatePatient = new SubjectIdMapper().fromFormValue(
    safeFormValue.subjectId,
    updatePatient,
  );
  updatePatient = new PatientNameMapper().fromFormValue(
    safeFormValue.name,
    updatePatient,
  );
  updatePatient = new AddressMapper().fromFormValue(
    safeFormValue.address,
    updatePatient,
  );
  updatePatient = new ContactPointMapper('email').fromFormValue(
    safeFormValue.email,
    updatePatient,
  );
  updatePatient = new ContactPointMapper('phone').fromFormValue(
    safeFormValue.phoneNumber,
    updatePatient,
  );
  updatePatient = new DateMapper('dob').fromFormValue(
    safeFormValue.dob,
    updatePatient,
  );
  updatePatient = new DateMapper('dod').fromFormValue(
    safeFormValue.dod,
    updatePatient,
  );
  updatePatient = new GenderMapper().fromFormValue(
    safeFormValue.gender,
    updatePatient,
  );
  updatePatient = new SubjectRaceMapper().fromFormValue(
    safeFormValue.race,
    updatePatient,
  );
  updatePatient = new SubjectGenderIdentityMapper().fromFormValue(
    safeFormValue.genderIdentity,
    updatePatient,
  );
  updatePatient = new SocialSecurityNumberMapper().fromFormValue(
    safeFormValue.socialSecurityNumber,
    updatePatient,
  );
  updatePatient = new MrnMapper(mrnConfig).fromFormValue(
    safeFormValue.mrn,
    updatePatient,
  );

  return updatePatient;
};
